package com.evoapps.evoshift.ui.kiosk

import androidx.lifecycle.compose.collectAsStateWithLifecycle
import evoshift.composeapp.generated.resources.clock_unplanned_extra_done
import evoshift.composeapp.generated.resources.clock_unplanned_cover_done
import evoshift.composeapp.generated.resources.qr_result_break_end
import evoshift.composeapp.generated.resources.qr_result_break_start
import evoshift.composeapp.generated.resources.qr_scan_break_button
import androidx.compose.material3.TextButton
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.QrCodeScanner
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.di.diViewModel
import com.evoapps.evoshift.domain.model.ClockAction
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.viewmodels.QrClockViewModel
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.DialogWidth
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.close
import evoshift.composeapp.generated.resources.qr_result_in
import evoshift.composeapp.generated.resources.qr_result_out
import evoshift.composeapp.generated.resources.qr_scan_button
import evoshift.composeapp.generated.resources.qr_scan_title
import org.jetbrains.compose.resources.stringResource

/** Test tag of the "Scan the tablet's code" button. */
const val SCAN_TABLET_CODE_TAG = "scan_tablet_code"

/** SH-116: test tag of "Start a break with the tablet's code". */
const val SCAN_TABLET_BREAK_TAG = "scan_tablet_break"

/**
 * SH-111: "Scan the tablet's code" under the clock button. [visible] when the person's clock
 * offers something now (start or end a shift), on devices that can scan. The result stays in a
 * dialog until closed, also after the button went (a clock-out finishes the day).
 */
@Composable
fun ScanTabletCodeButton(visible: Boolean, modifier: Modifier = Modifier, offerBreak: Boolean = false) {
    val viewModel: QrClockViewModel = diViewModel()
    val state by viewModel.uiState.collectAsStateWithLifecycle()
    if (!state.isAvailable) return

    if (visible || state.isBusy) {
        OutlinedButton(
            onClick = { viewModel.scan() },
            enabled = !state.isBusy,
            modifier = modifier.handCursor().testTag(SCAN_TABLET_CODE_TAG)
        ) {
            if (state.isBusy) CircularProgressIndicator(Modifier.size(18.dp), strokeWidth = 2.dp, color = DeepNavy)
            else Icon(Icons.Default.QrCodeScanner, contentDescription = null, tint = DeepNavy)
            Spacer(Modifier.width(8.dp))
            Text(stringResource(Res.string.qr_scan_button), color = DeepNavy)
        }
        // SH-116: while working, the code can also start a break (scanning during one ends it).
        if (offerBreak && !state.isBusy) {
            TextButton(
                onClick = { viewModel.scan(startBreak = true) },
                modifier = Modifier.handCursor().testTag(SCAN_TABLET_BREAK_TAG)
            ) {
                Text(stringResource(Res.string.qr_scan_break_button), color = DeepNavy)
            }
        }
    }

    val result = state.result
    val error = state.error
    if (result != null || error != null) {
        EvoDialog(
            title = stringResource(Res.string.qr_scan_title),
            onDismiss = viewModel::dismiss,
            icon = if (result != null) Icons.Default.CheckCircle else Icons.Default.QrCodeScanner,
            width = DialogWidth.Compact,
            dismiss = null,
            confirm = DialogAction(stringResource(Res.string.close), viewModel::dismiss)
        ) {
            Text(
                when {
                    result?.action == ClockAction.OUT -> stringResource(Res.string.qr_result_out, result.time)
                    result?.action == ClockAction.BREAK_START -> stringResource(Res.string.qr_result_break_start, result.time)
                    result?.action == ClockAction.BREAK_END -> stringResource(Res.string.qr_result_break_end, result.time)
                    result != null -> stringResource(Res.string.qr_result_in, result.time)
                    else -> error!!.asString()
                }
            )
            // SH-134: no shift of one's own: covering a colleague, or a new shift; a manager confirms.
            result?.unplanned?.let { unplanned ->
                Text(
                    if (unplanned.kind == com.evoapps.evoshift.domain.model.UnplannedKind.COVER) {
                        stringResource(Res.string.clock_unplanned_cover_done, unplanned.coveredName ?: "?")
                    } else stringResource(Res.string.clock_unplanned_extra_done)
                )
            }
        }
    }
}
